import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	ListItemText,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { GlobalState } from "context/GlobalContext";
import { rootProtectedPath, services } from "data/authData";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { isMboLfsQtyViewer } from "../data";
import {
	KpiStat,
	Panel,
	compactInputSx,
	outlinedButtonSx,
	primaryButtonSx,
} from "../sharedStyles";
import { downloadTable } from "../exportTable";

const COLUMN_GROUPS = [
	{
		id: "movement",
		label: "Movement",
		columns: [
			{ key: "dis_qty", label: "DIS QTY" },
			{ key: "rtv_qty", label: "RTV QTY" },
			{ key: "sec_sale_qty", label: "SEC-SALE QTY" },
			{ key: "net_qty", label: "NET QTY" },
		],
	},
	{
		id: "soh",
		label: "Stock on Hand",
		columns: [
			{ key: "cus_soh", label: "CUS-SOH" },
			{ key: "soh_net_qty", label: "NET QTY", highlight: true },
		],
	},
	{
		id: "rtpl_dis",
		label: "RTPL-DIS",
		columns: [
			{ key: "dis_git", label: "DIS-GIT", highlight: true },
			{ key: "rtv_git", label: "RTV-GIT", highlight: true },
		],
	},
	{
		id: "dis_ret",
		label: "DIS-RET",
		columns: [
			{ key: "dis_git_2", label: "DIS-GIT", highlight: true },
			{ key: "rtv_git_2", label: "RTV-GIT", highlight: true },
		],
	},
	{
		id: "git_net_qty",
		label: "NET QTY",
		spanRows: true,
		columns: [{ key: "git_net_qty", label: "NET QTY", highlight: true }],
	},
	{
		id: "settlement",
		label: "Settlement",
		columns: [
			{ key: "shortage_cn", label: "SHORTAGE-CN", highlight: true },
			{ key: "dispute_cn", label: "DISPUTE-CN", highlight: true },
		],
	},
];

const PINNED_VARIANCE = { key: "final_variance", label: "VARIANCE", highlight: true };

const FLAT_COLUMNS = COLUMN_GROUPS.flatMap((group) => group.columns);

const repeatedLabels = new Set(
	FLAT_COLUMNS.map((col) => col.label).filter((label, index, labels) => labels.indexOf(label) !== index)
);

const NET_KEYS = new Set(["net_qty", "soh_net_qty", "git_net_qty"]);

const TOTAL_COLUMNS = [
	...COLUMN_GROUPS.flatMap((group) =>
		group.columns
			.filter((col) => !NET_KEYS.has(col.key))
			.map((col) => ({
				key: col.key,
				label: repeatedLabels.has(col.label) ? `${group.label} ${col.label}` : col.label,
			}))
	),
	PINNED_VARIANCE,
];

const groupHeaderSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.5,
	textTransform: "uppercase",
	whiteSpace: "nowrap",
	bgcolor: "#f3f4f6",
	color: "#4b5563",
	borderBottom: "1px solid #e5e7eb",
	py: 0.75,
	px: 1.25,
	textAlign: "center",
	boxSizing: "border-box",
};

const HEADER_ROW_HEIGHT = 34;
const CUSTOMER_COL_MIN = 140;
const CUSTOMER_COL_MAX = 520;
const CUSTOMER_COL_DEFAULT = 220;

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.25,
	whiteSpace: "nowrap",
	bgcolor: "#fafafa",
	borderBottom: "1px solid #e5e7eb",
	py: 1,
	px: 1.25,
	color: "#374151",
	boxSizing: "border-box",
};

const bodyCellSx = {
	whiteSpace: "nowrap",
	py: 1.1,
	px: 1.25,
	fontSize: "0.8125rem",
	borderBottom: "1px solid #f1f5f9",
	color: "#111827",
};

const SELECT_ALL = "__select_all__";

const summaryView = {
	month: null,
	customer: [],
	rows: [],
	hasRun: false,
	page: 0,
	rowsPerPage: 25,
	customerColWidth: CUSTOMER_COL_DEFAULT,
};

const formatQty = (value) => {
	if (value === null || value === undefined || value === "") return "—";
	return Number(value).toLocaleString("en-IN");
};

const qtyColor = (value, highlight) => {
	if (!highlight || value === null || value === undefined || value === "") return "#111827";
	const num = Number(value);
	if (num === 0) return "#15803d";
	if (num < 0) return "#b91c1c";
	return "#b45309";
};

const compareCell = (a, b, numeric) => {
	const emptyA = a === null || a === undefined || a === "";
	const emptyB = b === null || b === undefined || b === "";
	if (emptyA && emptyB) return 0;
	if (emptyA) return 1;
	if (emptyB) return -1;
	if (numeric) return (Number(a) || 0) - (Number(b) || 0);
	return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

function SortHeader({ label, active, direction, align = "left", onClick }) {
	return (
		<TableSortLabel
			active={active}
			direction={active ? direction : "asc"}
			onClick={onClick}
			sx={{
				width: "100%",
				justifyContent: align === "right" ? "flex-end" : "flex-start",
				color: "inherit !important",
				fontWeight: "inherit",
				fontSize: "inherit",
				letterSpacing: "inherit",
				"&:hover": { color: "inherit" },
				"&.Mui-active": { color: "inherit" },
				"& .MuiTableSortLabel-icon": { fontSize: 14, color: "#6b7280 !important" },
			}}
		>
			{label}
		</TableSortLabel>
	);
}

function Summary() {
	const navigate = useNavigate();
	const { loginUser, setSnackBar, setSnackBarOpen } = GlobalState();
	const viewer = isMboLfsQtyViewer(loginUser);
	const [month, setMonth] = useState(() =>
		summaryView.month?.isValid?.() ? summaryView.month : dayjs().startOf("month")
	);
	const [selectedCustomers, setSelectedCustomers] = useState(() =>
		Array.isArray(summaryView.customer) ? summaryView.customer : []
	);
	const [customerQuery, setCustomerQuery] = useState("");
	const [customers, setCustomers] = useState([]);
	const [customersLoading, setCustomersLoading] = useState(false);
	const [rows, setRows] = useState(summaryView.rows);
	const [hasRun, setHasRun] = useState(summaryView.hasRun);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(summaryView.page);
	const [rowsPerPage, setRowsPerPage] = useState(summaryView.rowsPerPage);
	const [sort, setSort] = useState({ key: "", dir: "asc" });
	const [customerColWidth, setCustomerColWidth] = useState(
		summaryView.customerColWidth || CUSTOMER_COL_DEFAULT
	);
	const skipPageReset = useRef(true);
	const customerResizeCleanup = useRef(null);
	const loadToken = useRef(0);

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const openCustomerDetail = (customerName) => {
		if (!customerName || !month?.isValid?.()) return;
		const params = new URLSearchParams({
			customer: customerName,
			month: month.format("YYYY-MM"),
		});
		navigate(`/${rootProtectedPath}/${services.mbo_lfs_qty_reco.path}/detail?${params.toString()}`);
	};

	const changeMonth = (value) => {
		const next = value?.isValid?.() ? value.startOf("month") : value;
		const prevKey = month?.isValid?.() ? month.format("YYYY-MM") : "";
		const nextKey = next?.isValid?.() ? next.format("YYYY-MM") : "";
		setMonth(next);
		if (nextKey === prevKey) return;
		loadToken.current += 1;
		setRows([]);
		setPage(0);
		setHasRun(false);
		setLoading(false);
	};

	const loadSummary = useCallback(
		async (selectedMonth) => {
			if (!selectedMonth?.isValid?.()) {
				showSnack("Select a month", "error");
				return;
			}
			const token = ++loadToken.current;
			setLoading(true);
			setRows([]);
			setHasRun(false);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/summary", {
					params: { month: selectedMonth.format("YYYY-MM") },
				});
				if (loadToken.current !== token) return;
				setRows(res?.data?.result?.rows || []);
				setHasRun(true);
			} catch (err) {
				if (loadToken.current !== token) return;
				const data = err?.response?.data;
				const message =
					(typeof data === "string" && data.trim()) ||
					data?.message ||
					err?.message ||
					"Failed to load summary";
				showSnack(message, "error");
			} finally {
				if (loadToken.current === token) setLoading(false);
			}
		},
		[setSnackBar, setSnackBarOpen]
	);

	const customerOptions = useMemo(() => {
		const names = new Set(customers.filter(Boolean));
		rows.forEach((row) => {
			const name = String(row.customer_name || "").trim();
			if (name) names.add(name);
		});
		selectedCustomers.forEach((name) => {
			const trimmed = String(name || "").trim();
			if (trimmed) names.add(trimmed);
		});
		return Array.from(names).sort((left, right) =>
			left.localeCompare(right, undefined, { sensitivity: "base", numeric: true })
		);
	}, [customers, rows, selectedCustomers]);

	const visibleCustomers = useMemo(() => {
		const query = customerQuery.trim().toLowerCase();
		if (!query) return customerOptions;
		return customerOptions.filter((name) => name.toLowerCase().includes(query));
	}, [customerOptions, customerQuery]);

	const allVisibleSelected =
		visibleCustomers.length > 0 &&
		visibleCustomers.every((name) => selectedCustomers.includes(name));
	const someVisibleSelected =
		visibleCustomers.some((name) => selectedCustomers.includes(name)) && !allVisibleSelected;

	const toggleSelectAll = () => {
		if (!visibleCustomers.length) return;
		if (allVisibleSelected) {
			const visible = new Set(visibleCustomers);
			setSelectedCustomers((current) => current.filter((name) => !visible.has(name)));
			return;
		}
		setSelectedCustomers((current) => {
			const next = new Set(current);
			visibleCustomers.forEach((name) => next.add(name));
			return customerOptions.filter((name) => next.has(name));
		});
	};

	const filteredRows = useMemo(() => {
		if (!selectedCustomers.length) return rows;
		const selected = new Set(selectedCustomers.map((name) => String(name).trim().toLowerCase()));
		return rows.filter((row) => selected.has(String(row.customer_name || "").trim().toLowerCase()));
	}, [rows, selectedCustomers]);

	const sortedRows = useMemo(() => {
		if (!sort.key) return filteredRows;
		const numeric = sort.key !== "customer_name";
		const dir = sort.dir === "desc" ? -1 : 1;
		return [...filteredRows].sort(
			(left, right) => compareCell(left[sort.key], right[sort.key], numeric) * dir
		);
	}, [filteredRows, sort]);

	const toggleSort = (key, numeric) => {
		setSort((current) =>
			current.key === key
				? { key, dir: current.dir === "asc" ? "desc" : "asc" }
				: { key, dir: numeric ? "desc" : "asc" }
		);
		setPage(0);
	};

	const customerColSizeSx = {
		width: customerColWidth,
		minWidth: customerColWidth,
		maxWidth: customerColWidth,
	};

	const startCustomerResize = (event) => {
		event.preventDefault();
		event.stopPropagation();
		const startX = event.clientX;
		const startWidth = customerColWidth;
		const previousCursor = document.body.style.cursor;
		const previousSelect = document.body.style.userSelect;
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		const onMove = (moveEvent) => {
			const next = Math.min(
				CUSTOMER_COL_MAX,
				Math.max(CUSTOMER_COL_MIN, startWidth + moveEvent.clientX - startX)
			);
			setCustomerColWidth(next);
		};
		const onUp = () => {
			document.body.style.cursor = previousCursor;
			document.body.style.userSelect = previousSelect;
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
			customerResizeCleanup.current = null;
		};
		customerResizeCleanup.current?.();
		customerResizeCleanup.current = onUp;
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
	};

	useEffect(() => () => customerResizeCleanup.current?.(), []);

	useEffect(() => {
		let active = true;
		(async () => {
			setCustomersLoading(true);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/customers");
				if (active) setCustomers(res?.data?.result?.customers || []);
			} catch {
				if (active) setCustomers([]);
			} finally {
				if (active) setCustomersLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		summaryView.month = month;
		summaryView.customer = selectedCustomers;
		summaryView.rows = rows;
		summaryView.hasRun = hasRun;
		summaryView.page = page;
		summaryView.rowsPerPage = rowsPerPage;
		summaryView.customerColWidth = customerColWidth;
	}, [month, selectedCustomers, rows, hasRun, page, rowsPerPage, customerColWidth]);

	useEffect(() => {
		if (skipPageReset.current) {
			skipPageReset.current = false;
			return;
		}
		setPage(0);
	}, [selectedCustomers, rows]);

	const pagedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	const columnTotals = useMemo(() => {
		const sum = (key) => filteredRows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
		return TOTAL_COLUMNS.map((col) => ({ ...col, value: sum(col.key) }));
	}, [filteredRows]);

	return (
		<Box>
			{/* Toolbar */}
			<Panel sx={{ px: 2, py: 1.5, mb: 1.5 }}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack
						direction={{ xs: "column", lg: "row" }}
						spacing={1.25}
						alignItems={{ xs: "stretch", lg: "center" }}
						justifyContent="space-between"
					>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={1.25}
							alignItems={{ xs: "stretch", sm: "center" }}
							flexWrap="wrap"
							useFlexGap
						>
							<DatePicker
								label="Month"
								views={["year", "month"]}
								openTo="month"
								value={month}
								onChange={changeMonth}
								format="MMM YYYY"
								slotProps={{
									textField: {
										size: "small",
										sx: {
											width: { xs: "100%", sm: 160 },
											flexShrink: 0,
											...compactInputSx,
										},
									},
								}}
							/>
							<Autocomplete
								multiple
								disableCloseOnSelect
								size="small"
								options={[SELECT_ALL, ...customerOptions]}
								value={selectedCustomers}
								isOptionEqualToValue={(option, value) => option === value}
								getOptionLabel={(option) => (option === SELECT_ALL ? "Select all" : option)}
								filterOptions={(_options, state) => {
									const query = state.inputValue.trim().toLowerCase();
									const matched = customerOptions.filter(
										(name) => !query || name.toLowerCase().includes(query)
									);
									return matched.length ? [SELECT_ALL, ...matched] : [];
								}}
								onChange={(_, value, reason) => {
									if (reason === "clear" || value.includes(SELECT_ALL)) {
										if (value.includes(SELECT_ALL)) toggleSelectAll();
										else setSelectedCustomers([]);
										return;
									}
									setSelectedCustomers(value);
								}}
								onInputChange={(_, value) => setCustomerQuery(value)}
								loading={customersLoading}
								sx={{
									width: { xs: "100%", sm: 360 },
									flexShrink: 0,
									"& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" },
								}}
								renderOption={(props, option) => {
									const { key: _optionKey, ...optionProps } = props;
									const isAll = option === SELECT_ALL;
									return (
										<li key={option} {...optionProps}>
											<Checkbox
												size="small"
												checked={isAll ? allVisibleSelected : selectedCustomers.includes(option)}
												indeterminate={isAll && someVisibleSelected}
												sx={{ p: 0.5, mr: 1 }}
											/>
											<ListItemText
												primary={
													isAll
														? customerQuery.trim()
															? `Select all matching (${visibleCustomers.length})`
															: "Select all"
														: option
												}
												primaryTypographyProps={{
													fontSize: "0.8125rem",
													fontWeight: isAll ? 600 : 400,
												}}
											/>
										</li>
									);
								}}
								renderTags={(value, getTagProps) => {
									if (value.length === customerOptions.length && customerOptions.length > 0) {
										return (
											<Chip
												label="All customers"
												size="small"
												onDelete={() => setSelectedCustomers([])}
											/>
										);
									}
									return value.slice(0, 1).map((option, index) => (
										<Chip
											{...getTagProps({ index })}
											key={option}
											label={value.length > 1 ? `${option} +${value.length - 1}` : option}
											size="small"
											sx={{ maxWidth: 220 }}
										/>
									));
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Customer"
										placeholder={selectedCustomers.length ? "" : "All customers"}
										sx={compactInputSx}
									/>
								)}
							/>
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<Button
								variant="contained"
								size="small"
								disabled={loading}
								onClick={() => loadSummary(month)}
								startIcon={
									loading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon />
								}
								sx={{ ...primaryButtonSx, height: 36 }}
							>
								{loading ? "Running..." : "Run"}
							</Button>
							{viewer ? null : (
							<Button
								variant="outlined"
								size="small"
								disabled={loading || filteredRows.length === 0}
								startIcon={<DownloadOutlinedIcon />}
								onClick={() => {
									const columns = [
										{ key: "customer_name", label: "CUSTOMER NAME" },
										...COLUMN_GROUPS.flatMap((group) =>
											group.columns.map((col) => ({
												key: col.key,
												label: repeatedLabels.has(col.label) ? `${group.label} ${col.label}` : col.label,
											}))
										),
										{ key: PINNED_VARIANCE.key, label: PINNED_VARIANCE.label },
									];
									const monthName = month?.isValid?.() ? month.format("YYYY-MM") : "summary";
									downloadTable(`mbo_lfs_summary_${monthName}.xlsx`, columns, sortedRows);
								}}
								sx={{ ...outlinedButtonSx, height: 36 }}
							>
								Export
							</Button>
							)}
						</Stack>
					</Stack>
				</LocalizationProvider>
			</Panel>

			{/* KPI strip */}
			<Panel sx={{ mb: 1.5, display: "flex", flexWrap: "nowrap", width: "100%", overflow: "hidden" }}>
				<KpiStat
					label="Customers"
					value={hasRun ? filteredRows.length.toLocaleString("en-IN") : "—"}
				/>
				{columnTotals.map((col) => (
					<KpiStat
						key={col.key}
						label={col.label}
						value={hasRun ? col.value.toLocaleString("en-IN") : "—"}
						tone={
							hasRun && col.key === "final_variance"
								? col.value === 0
									? "success"
									: "danger"
								: "neutral"
						}
					/>
				))}
			</Panel>

			{/* Table */}
			<Panel sx={{ overflow: "hidden" }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef0f3" }}
				>
					<Typography variant="subtitle2" fontWeight={700} color="#111827">
						Customer Qty Summary
					</Typography>
					<Chip
						label={
							hasRun
								? `${filteredRows.length} ${filteredRows.length === 1 ? "customer" : "customers"}`
								: "0 customers"
						}
						size="small"
						sx={{
							height: 24,
							fontWeight: 600,
							fontSize: "0.7rem",
							bgcolor: "#f3f4f6",
							color: "#374151",
						}}
					/>
				</Stack>

				<TableContainer sx={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
					<Table
						stickyHeader
						size="small"
						sx={{ minWidth: 1460 + customerColWidth, width: 1460 + customerColWidth }}
					>
						<colgroup>
							<col style={{ width: customerColWidth }} />
						</colgroup>
						<TableHead>
							<TableRow>
								<TableCell
									sortDirection={sort.key === "customer_name" ? sort.dir : false}
									sx={{
										...headerCellSx,
										position: "sticky",
										left: 0,
										top: 0,
										zIndex: 8,
										height: HEADER_ROW_HEIGHT,
										...customerColSizeSx,
										py: 0,
										lineHeight: `${HEADER_ROW_HEIGHT}px`,
										bgcolor: "#fafafa",
										borderRight: "1px solid #e5e7eb",
										borderBottom: 0,
										boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: 0 },
									}}
								>
									<SortHeader
										label="CUSTOMER NAME"
										active={sort.key === "customer_name"}
										direction={sort.dir}
										onClick={() => toggleSort("customer_name", false)}
									/>
									<Box
										onMouseDown={startCustomerResize}
										onClick={(clickEvent) => clickEvent.stopPropagation()}
										sx={{
											position: "absolute",
											top: 0,
											right: 0,
											width: 8,
											height: HEADER_ROW_HEIGHT * 2,
											cursor: "col-resize",
											zIndex: 8,
											"&:hover": { bgcolor: "rgba(37, 99, 235, 0.35)" },
										}}
									/>
								</TableCell>
								{COLUMN_GROUPS.map((group) =>
									group.spanRows ? (
										<TableCell
											key={group.id}
											align="right"
											sortDirection={sort.key === group.columns[0].key ? sort.dir : false}
											sx={{
												...headerCellSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: `${HEADER_ROW_HEIGHT}px`,
												borderLeft: "1px solid #e5e7eb",
												borderBottom: 0,
												minWidth: 96,
												bgcolor: "#fafafa",
												"&.MuiTableCell-stickyHeader": { top: 0 },
											}}
										>
											<SortHeader
												label={group.label}
												align="right"
												active={sort.key === group.columns[0].key}
												direction={sort.dir}
												onClick={() => toggleSort(group.columns[0].key, true)}
											/>
										</TableCell>
									) : (
										<TableCell
											key={group.id}
											align="center"
											colSpan={group.columns.length}
											sx={{
												...groupHeaderSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: `${HEADER_ROW_HEIGHT}px`,
												borderLeft: "1px solid #e5e7eb",
												"&.MuiTableCell-stickyHeader": { top: 0 },
											}}
										>
											{group.label}
										</TableCell>
									)
								)}
								<TableCell
									align="right"
									sortDirection={sort.key === PINNED_VARIANCE.key ? sort.dir : false}
									sx={{
										...headerCellSx,
										position: "sticky",
										right: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										minWidth: 108,
										width: 108,
										maxWidth: 108,
										px: 1,
										py: 0,
										lineHeight: `${HEADER_ROW_HEIGHT}px`,
										bgcolor: "#fafafa",
										borderLeft: "1px solid #e5e7eb",
										borderBottom: 0,
										boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: 0 },
									}}
								>
									<SortHeader
										label={PINNED_VARIANCE.label}
										align="right"
										active={sort.key === PINNED_VARIANCE.key}
										direction={sort.dir}
										onClick={() => toggleSort(PINNED_VARIANCE.key, true)}
									/>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									sx={{
										...headerCellSx,
										position: "sticky",
										left: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										...customerColSizeSx,
										py: 0,
										bgcolor: "#fafafa",
										borderRight: "1px solid #e5e7eb",
										boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
									}}
								/>
								{FLAT_COLUMNS.map((col, index) => {
									const spanned = COLUMN_GROUPS.some(
										(group) => group.spanRows && group.columns.some((item) => item.key === col.key)
									);
									return (
										<TableCell
											key={`${col.key}-${index}`}
											align="right"
											sortDirection={!spanned && sort.key === col.key ? sort.dir : false}
											sx={{
												...headerCellSx,
												height: HEADER_ROW_HEIGHT,
												py: 0,
												lineHeight: spanned ? undefined : `${HEADER_ROW_HEIGHT}px`,
												bgcolor: "#fafafa",
												borderLeft:
													index === 0 || COLUMN_GROUPS.some((g) => g.columns[0].key === col.key)
														? "1px solid #e5e7eb"
														: undefined,
												"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
											}}
										>
											{spanned ? (
												""
											) : (
												<SortHeader
													label={col.label}
													align="right"
													active={sort.key === col.key}
													direction={sort.dir}
													onClick={() => toggleSort(col.key, true)}
												/>
											)}
										</TableCell>
									);
								})}
								<TableCell
									sx={{
										...headerCellSx,
										position: "sticky",
										right: 0,
										zIndex: 6,
										height: HEADER_ROW_HEIGHT,
										minWidth: 108,
										width: 108,
										maxWidth: 108,
										py: 0,
										bgcolor: "#fafafa",
										borderLeft: "1px solid #e5e7eb",
										boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
										"&.MuiTableCell-stickyHeader": { top: HEADER_ROW_HEIGHT },
									}}
								/>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={FLAT_COLUMNS.length + 2} align="center" sx={{ py: 8, borderBottom: 0 }}>
										<Stack alignItems="center" spacing={1.25}>
											<CircularProgress size={28} />
											<Typography color="text.secondary" variant="body2">
												Loading...
											</Typography>
										</Stack>
									</TableCell>
								</TableRow>
							) : (
								pagedRows.map((row) => (
								<TableRow
									key={row.id}
									hover
									sx={{
										"&:hover td": { bgcolor: "#f9fafb" },
										"&:last-child td": { borderBottom: 0 },
									}}
								>
									<TableCell
										sx={{
											...bodyCellSx,
											position: "sticky",
											left: 0,
											zIndex: 2,
											bgcolor: "#fff",
											fontWeight: 600,
											...customerColSizeSx,
											overflow: "hidden",
											borderRight: "1px solid #eef0f3",
											boxShadow: "2px 0 6px rgba(15,23,42,0.04)",
										}}
									>
										<Tooltip title={row.customer_name} placement="top" enterDelay={250}>
											<Box
												component="button"
												type="button"
												onClick={() => openCustomerDetail(row.customer_name)}
												sx={{
													display: "block",
													width: "100%",
													maxWidth: "100%",
													p: 0,
													border: 0,
													bgcolor: "transparent",
													cursor: "pointer",
													font: "inherit",
													fontWeight: 600,
													fontSize: "0.8125rem",
													color: "#1d4ed8",
													textAlign: "left",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{row.customer_name}
											</Box>
										</Tooltip>
									</TableCell>
									{FLAT_COLUMNS.map((col, index) => {
										const value = row[col.key];
										const isGroupStart =
											index === 0 || COLUMN_GROUPS.some((g) => g.columns[0].key === col.key);
										return (
											<TableCell
												key={col.key}
												align="right"
												sx={{
													...bodyCellSx,
													borderLeft: isGroupStart ? "1px solid #eef0f3" : undefined,
													fontWeight: col.highlight ? 600 : 500,
													color: qtyColor(value, col.highlight),
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{formatQty(value)}
											</TableCell>
										);
									})}
									<TableCell
										align="right"
										sx={{
											...bodyCellSx,
											position: "sticky",
											right: 0,
											zIndex: 2,
											bgcolor: "#fff",
											fontWeight: 700,
											minWidth: 108,
											width: 108,
											maxWidth: 108,
											px: 1,
											borderLeft: "1px solid #eef0f3",
											boxShadow: "-2px 0 6px rgba(15,23,42,0.04)",
											color: qtyColor(row[PINNED_VARIANCE.key], true),
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{formatQty(row[PINNED_VARIANCE.key])}
									</TableCell>
								</TableRow>
							))
							)}
							{!loading && filteredRows.length === 0 && (
								<TableRow>
									<TableCell colSpan={FLAT_COLUMNS.length + 2} align="center" sx={{ py: 6 }}>
										<Typography color="text.secondary">
											{!hasRun
												? "Click Run to load this month"
												: selectedCustomers.length
													? "No data for the selected customers"
													: "No customers found"}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					onPageChange={(_, nextPage) => setPage(nextPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[25, 50, 100]}
				/>
			</Panel>
		</Box>
	);
}

export default Summary;
